import React, { useState, useEffect } from 'react';
import { 
  Eye, 
  TrendingUp, 
  Users, 
  Package,
  Calendar,
  ChevronDown,
  BarChart3,
  ArrowUp,
  ArrowDown,
  Tag,
  DollarSign,
  Percent,
  ShoppingBag,
  Gift
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('products'); // 'products' or 'coupons'
  const [couponData, setCouponData] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'products') {
      loadAnalytics();
    } else {
      loadCouponStats();
    }
  }, [days, activeTab]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/analytics/admin/product-views?days=${days}&limit=20`);
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        setError('Error al cargar datos');
      }
    } catch (e) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const loadCouponStats = async () => {
    try {
      setCouponLoading(true);
      const response = await fetch(`${API_URL}/api/analytics/admin/coupon-stats?days=${days}`);
      if (response.ok) {
        const result = await response.json();
        setCouponData(result);
      } else {
        setError('Error al cargar datos de cupones');
      }
    } catch (e) {
      setError('Error de conexión');
    } finally {
      setCouponLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price);
  };

  // Calculate max views for bar chart scaling
  const maxDailyViews = data?.daily_views?.length > 0 
    ? Math.max(...data.daily_views.map(d => d.views)) 
    : 1;

  return (
    <div className="p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-neon-cyan" />
              Analytics
            </h1>
            <p className="text-white/50 text-sm mt-1">
              Analítica de productos y rendimiento de cupones
            </p>
          </div>
          
          {/* Period Selector */}
          <div className="relative">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="appearance-none bg-white/5 border border-white/10 rounded-lg px-4 py-2 pr-10 text-white focus:outline-none focus:border-neon-cyan"
            >
              <option value={7}>Últimos 7 días</option>
              <option value={14}>Últimos 14 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={90}>Últimos 90 días</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50 pointer-events-none" />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 pb-2">
          <button
            onClick={() => setActiveTab('products')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'products'
                ? 'bg-neon-cyan text-neon-dark'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            <Eye size={18} />
            Productos
          </button>
          <button
            onClick={() => setActiveTab('coupons')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'coupons'
                ? 'bg-neon-pink text-neon-dark'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            <Tag size={18} />
            Cupones e Influencers
          </button>
        </div>

        {/* Products Tab Content */}
        {activeTab === 'products' && (
          <>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-neon-cyan border-t-transparent rounded-full animate-spin" />
              </div>
            ) : error ? (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
                {error}
              </div>
            ) : data ? (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-neon-cyan/20 flex items-center justify-center">
                        <Eye className="w-5 h-5 text-neon-cyan" />
                      </div>
                      <span className="text-white/50 text-sm">Total Vistas</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{data.total_views?.toLocaleString() || 0}</p>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-neon-pink/20 flex items-center justify-center">
                        <Package className="w-5 h-5 text-neon-pink" />
                      </div>
                      <span className="text-white/50 text-sm">Productos Vistos</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{data.top_products?.length || 0}</p>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-400" />
                      </div>
                      <span className="text-white/50 text-sm">Vistas Hombre</span>
                    </div>
                    <p className="text-3xl font-bold text-white">
                      {data.views_by_gender?.find(g => g.gender === 'Hombre')?.count?.toLocaleString() || 0}
                    </p>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
                        <Users className="w-5 h-5 text-pink-400" />
                      </div>
                      <span className="text-white/50 text-sm">Vistas Mujer</span>
                    </div>
                    <p className="text-3xl font-bold text-white">
                      {data.views_by_gender?.find(g => g.gender === 'Mujer')?.count?.toLocaleString() || 0}
                    </p>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                        <Users className="w-5 h-5 text-white/70" />
                      </div>
                      <span className="text-white/50 text-sm">Vistas Unisex</span>
                    </div>
                    <p className="text-3xl font-bold text-white">
                      {data.views_by_gender?.find(g => g.gender === 'Unisex')?.count?.toLocaleString() || 0}
                    </p>
                  </div>
                </div>

                {/* Daily Views Chart */}
                {data.daily_views?.length > 0 && (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                    <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-neon-cyan" />
                      Vistas por Día
                    </h2>
                    <div className="flex items-end gap-1 h-40">
                      {data.daily_views.map((day, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <span className="text-xs text-white/50">{day.views}</span>
                          <div 
                            className="w-full bg-gradient-to-t from-neon-cyan/80 to-neon-cyan/40 rounded-t transition-all duration-300 hover:from-neon-cyan hover:to-neon-cyan/60"
                            style={{ height: `${(day.views / maxDailyViews) * 100}%`, minHeight: '4px' }}
                          />
                          <span className="text-[10px] text-white/40 rotate-0 md:rotate-0">
                            {formatDate(day.date)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Products Table */}
                <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                  <div className="p-4 md:p-6 border-b border-white/10">
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Eye className="w-5 h-5 text-neon-pink" />
                      Productos Más Vistos
                    </h2>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left text-xs text-white/50 font-medium p-4">#</th>
                          <th className="text-left text-xs text-white/50 font-medium p-4">Producto</th>
                          <th className="text-left text-xs text-white/50 font-medium p-4 hidden md:table-cell">Marca</th>
                          <th className="text-left text-xs text-white/50 font-medium p-4 hidden lg:table-cell">Género</th>
                          <th className="text-right text-xs text-white/50 font-medium p-4">Vistas</th>
                          <th className="text-right text-xs text-white/50 font-medium p-4 hidden sm:table-cell">Únicos</th>
                          <th className="text-right text-xs text-white/50 font-medium p-4 hidden md:table-cell">Precio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.top_products?.map((product, index) => (
                          <tr 
                            key={product.product_id} 
                            className="border-b border-white/5 hover:bg-white/5 transition-colors"
                          >
                            <td className="p-4">
                              <span className={`
                                w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                                ${index === 0 ? 'bg-yellow-500/20 text-yellow-400' : 
                                  index === 1 ? 'bg-gray-400/20 text-gray-300' : 
                                  index === 2 ? 'bg-amber-600/20 text-amber-500' : 
                                  'bg-white/10 text-white/50'}
                              `}>
                                {index + 1}
                              </span>
                            </td>
                            <td className="p-4">
                              <div>
                                <p className="text-white font-medium text-sm">{product.product_code}</p>
                                <p className="text-white/50 text-xs truncate max-w-[200px]">{product.product_name}</p>
                              </div>
                            </td>
                            <td className="p-4 hidden md:table-cell">
                              <span className="text-white/70 text-sm">{product.brand || '-'}</span>
                            </td>
                            <td className="p-4 hidden lg:table-cell">
                              <span className={`
                                px-2 py-1 rounded text-xs font-medium
                                ${product.gender === 'Mujer' ? 'bg-neon-pink/20 text-neon-pink' : 'bg-neon-cyan/20 text-neon-cyan'}
                              `}>
                                {product.gender || '-'}
                              </span>
                            </td>
                            <td className="p-4 text-right">
                              <span className="text-white font-semibold">{product.view_count?.toLocaleString()}</span>
                            </td>
                            <td className="p-4 text-right hidden sm:table-cell">
                              <span className="text-white/70">{product.unique_visitors?.toLocaleString()}</span>
                            </td>
                            <td className="p-4 text-right hidden md:table-cell">
                              <span className="text-neon-cyan font-medium">{formatPrice(product.price)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {(!data.top_products || data.top_products.length === 0) && (
                    <div className="p-12 text-center">
                      <Eye className="w-12 h-12 text-white/20 mx-auto mb-4" />
                      <p className="text-white/50">No hay datos de vistas aún</p>
                      <p className="text-white/30 text-sm mt-1">Las vistas de productos aparecerán aquí</p>
                    </div>
                  )}
                </div>

                {/* Brands Chart */}
                {data.views_by_brand?.length > 0 && (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                    <h2 className="text-lg font-semibold text-white mb-4">
                      Top Marcas Más Buscadas
                    </h2>
                    <div className="space-y-3">
                      {data.views_by_brand.slice(0, 10).map((brand, i) => {
                        const maxBrandViews = data.views_by_brand[0]?.count || 1;
                        const percentage = (brand.count / maxBrandViews) * 100;
                        return (
                          <div key={i} className="flex items-center gap-3">
                            <span className="text-white/70 text-sm w-32 truncate">{brand.brand}</span>
                            <div className="flex-1 h-6 bg-white/5 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-neon-pink to-neon-cyan rounded-full transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-white font-medium text-sm w-16 text-right">
                              {brand.count}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </>
        )}

        {/* Coupons Tab Content */}
        {activeTab === 'coupons' && (
          <>
            {couponLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-neon-pink border-t-transparent rounded-full animate-spin" />
              </div>
            ) : error ? (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
                {error}
              </div>
            ) : couponData ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-neon-pink/20 flex items-center justify-center">
                        <ShoppingBag className="w-5 h-5 text-neon-pink" />
                      </div>
                      <span className="text-white/50 text-sm">Pedidos con Cupón</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{couponData.summary?.total_orders_with_coupons || 0}</p>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-400" />
                      </div>
                      <span className="text-white/50 text-sm">Ventas con Cupón</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{formatPrice(couponData.summary?.total_sales_with_coupons || 0)}</p>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                        <Gift className="w-5 h-5 text-amber-400" />
                      </div>
                      <span className="text-white/50 text-sm">Descuentos Otorgados</span>
                    </div>
                    <p className="text-3xl font-bold text-amber-400">{formatPrice(couponData.summary?.total_discounts_given || 0)}</p>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                        <Users className="w-5 h-5 text-purple-400" />
                      </div>
                      <span className="text-white/50 text-sm">Comisiones a Pagar</span>
                    </div>
                    <p className="text-3xl font-bold text-purple-400">{formatPrice(couponData.summary?.total_commissions_owed || 0)}</p>
                  </div>
                </div>

                {/* Coupons Table */}
                <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                  <div className="p-4 md:p-6 border-b border-white/10">
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Tag className="w-5 h-5 text-neon-pink" />
                      Rendimiento por Cupón
                    </h2>
                    <p className="text-white/40 text-sm mt-1">
                      {couponData.summary?.coupons_used_count || 0} cupones usados de {couponData.summary?.active_coupons_count || 0} activos
                    </p>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left text-xs text-white/50 font-medium p-4">Cupón</th>
                          <th className="text-left text-xs text-white/50 font-medium p-4">Influencer</th>
                          <th className="text-right text-xs text-white/50 font-medium p-4">Pedidos</th>
                          <th className="text-right text-xs text-white/50 font-medium p-4">Perfumes</th>
                          <th className="text-right text-xs text-white/50 font-medium p-4">Ventas</th>
                          <th className="text-right text-xs text-white/50 font-medium p-4">Descuento</th>
                          <th className="text-right text-xs text-white/50 font-medium p-4">Comisión</th>
                        </tr>
                      </thead>
                      <tbody>
                        {couponData.coupons?.map((coupon, index) => (
                          <tr 
                            key={coupon.coupon_code} 
                            className="border-b border-white/5 hover:bg-white/5 transition-colors"
                          >
                            <td className="p-4">
                              <span className="px-3 py-1 bg-neon-pink/20 text-neon-pink rounded-full font-mono font-bold text-sm">
                                {coupon.coupon_code}
                              </span>
                            </td>
                            <td className="p-4">
                              {coupon.influencer ? (
                                <div>
                                  <p className="text-white font-medium text-sm">{coupon.influencer.name}</p>
                                  <p className="text-white/40 text-xs flex items-center gap-1">
                                    {coupon.influencer.commission_type === 'fixed' ? (
                                      <><DollarSign size={10} />{coupon.influencer.commission_value}/perfume</>
                                    ) : (
                                      <><Percent size={10} />{coupon.influencer.commission_value}%</>
                                    )}
                                  </p>
                                </div>
                              ) : (
                                <span className="text-white/30 text-sm">Sin influencer</span>
                              )}
                            </td>
                            <td className="p-4 text-right">
                              <span className="text-white font-semibold">{coupon.total_orders}</span>
                              {coupon.orders_in_period > 0 && (
                                <span className="text-green-400 text-xs ml-1">(+{coupon.orders_in_period})</span>
                              )}
                            </td>
                            <td className="p-4 text-right">
                              <span className="text-white">{coupon.total_perfumes}</span>
                            </td>
                            <td className="p-4 text-right">
                              <span className="text-white font-semibold">{formatPrice(coupon.total_sales)}</span>
                            </td>
                            <td className="p-4 text-right">
                              <span className="text-amber-400">{formatPrice(coupon.total_discount_given)}</span>
                            </td>
                            <td className="p-4 text-right">
                              <span className="text-purple-400 font-semibold">{formatPrice(coupon.commission_earned)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {(!couponData.coupons || couponData.coupons.length === 0) && (
                    <div className="p-12 text-center">
                      <Tag className="w-12 h-12 text-white/20 mx-auto mb-4" />
                      <p className="text-white/50">No hay ventas con cupones aún</p>
                      <p className="text-white/30 text-sm mt-1">Las ventas con cupones aparecerán aquí</p>
                    </div>
                  )}
                </div>

                {/* Daily Chart */}
                {couponData.daily_chart?.length > 0 && (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 md:p-6">
                    <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-neon-pink" />
                      Ventas con Cupón por Día
                    </h2>
                    <div className="flex items-end gap-1 h-40">
                      {couponData.daily_chart.map((day, i) => {
                        const maxSales = Math.max(...couponData.daily_chart.map(d => d.sales)) || 1;
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1">
                            <span className="text-xs text-white/50">{day.orders}</span>
                            <div 
                              className="w-full bg-gradient-to-t from-neon-pink/80 to-neon-pink/40 rounded-t transition-all duration-300 hover:from-neon-pink hover:to-neon-pink/60"
                              style={{ height: `${(day.sales / maxSales) * 100}%`, minHeight: '4px' }}
                              title={`${formatPrice(day.sales)}`}
                            />
                            <span className="text-[10px] text-white/40">
                              {formatDate(day.date)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </>
        )}
      </div>
  );
};

export default AdminAnalytics;
